import invariant from 'invariant';
import graphql from 'api/graphql';
import { fetchGigyaJWTPromise } from 'api/gigya';

import { GIGYA_BRAND_IMAGE_FAVORITES } from 'globalConst/gigya-const';

import * as analytics from 'analytics';
import { navigateToLoginScreen } from 'actions/gigya-router-actions';
import { showNotification } from 'actions/notifications-actions';
import { NotificationTypes, NotificationTexts } from 'components/Notifications/Notification';

import { favoriteActions, favoriteClipActions } from './actions-const';
import logger from 'util/logger';

/**
 * Add to favorite clip
 * @param  {object} item
 */
function addFavoriteClip(item) {
  return async (dispatch, getState) => {
    const {
      favorites,
      user: { isLoggedIn },
    } = getState();
    const dispatchType = isLoggedIn ? favoriteClipActions.FAVORITE_CLIP : favoriteClipActions.QUEUE_FAVORITE_CLIP;

    dispatch({
      type: dispatchType,
      payload: {
        id: item.id,
        contentTypeId: item.contentTypeId,
        updatedAt: Date.now(),
      },
    });

    if (isLoggedIn) {
      try {
        const jwtToken = await fetchGigyaJWTPromise();
        await graphql.mutateFavorite(jwtToken, item.id, item.contentTypeId, true);

        dispatch(
          showNotification({
            message: NotificationTexts.CLIP_ADD_TO_COLLECTION,
          })
        );

        analytics.trackFavoriteClip(item);
      } catch (e) {
        // rollback
        dispatch({
          type: favoriteActions.ROLLBACK_FAVORITES,
          payload: favorites,
        });

        dispatch(
          showNotification({
            message: NotificationTexts.CLIP_ADD_TO_COLLECTION_ERROR,
            type: NotificationTypes.ERROR,
          })
        );
        logger.error(e);
      }
    }
  };
}

/**
 * Remove favorite clip
 * @param  {object} item
 */
function removefavoriteClip(item) {
  return async (dispatch, getState) => {
    const { favorites } = getState();

    // optimistic UI
    dispatch({
      type: favoriteClipActions.UNFAVORITE_CLIP,
      payload: item.id,
    });

    try {
      const jwtToken = await fetchGigyaJWTPromise();

      await graphql.mutateFavorite(jwtToken, item.id, item.contentTypeId, false);

      dispatch(
        showNotification({
          message: NotificationTexts.CLIP_REMOVE_FROM_COLLECTION,
        })
      );

      analytics.trackUnfavoriteClip(item);
    } catch (e) {
      // rollback
      dispatch({
        type: favoriteActions.ROLLBACK_FAVORITES,
        payload: favorites,
      });

      dispatch(
        showNotification({
          message: NotificationTexts.CLIP_REMOVE_FROM_COLLECTION_ERROR,
          type: NotificationTypes.ERROR,
        })
      );
      logger.error(e);
    }
  };
}

/**
 * Toggle Add/Remove clip from favorites
 * @param {object} item
 */
export function favoriteClipToggle(item) {
  return async (dispatch, getState) => {
    const {
      favorites: { clips: currentFavoriteClips },
      user: { isLoggedIn, signature },
    } = getState();

    if (!isLoggedIn) {
      analytics.gigyaExpand('add-my-category');
      navigateToLoginScreen(GIGYA_BRAND_IMAGE_FAVORITES);
    } else {
      invariant(signature, 'No signature found.');
    }

    const isFavorite = currentFavoriteClips.some((favorite) => favorite.id === item.id);

    if (isFavorite) {
      dispatch(removefavoriteClip(item));
    } else {
      dispatch(addFavoriteClip(item));
    }
  };
}

export function loadQueuedFavoriteClip() {
  return async (dispatch, getState) => {
    const {
      favorites,
      user: { isLoggedIn },
    } = getState();

    if (!isLoggedIn) {
      return;
    }

    const queuedClip = favorites.queue.clips || [];

    await Promise.all(
      queuedClip.map(async (item) => {
        try {
          const jwtToken = await fetchGigyaJWTPromise();

          await graphql.mutateFavorite(jwtToken, item.id, item.contentTypeId, true);

          dispatch({
            type: favoriteClipActions.FAVORITE_CLIP,
            payload: item,
          });

          dispatch(
            showNotification({
              message: NotificationTexts.CLIP_ADD_TO_COLLECTION,
            })
          );
        } catch (e) {
          // rollback
          dispatch({
            type: favoriteActions.ROLLBACK_FAVORITES,
            payload: favorites,
          });

          dispatch(
            showNotification({
              message: NotificationTexts.CLIP_ADD_TO_COLLECTION_ERROR,
              type: NotificationTypes.ERROR,
            })
          );
          logger.error(e);
        }
      })
    );
  };
}
